import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import {
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import t from "@/lang/lang";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { Separator } from "@/shared/components/ui/separator";
import { ReactNode, useEffect, useState } from "react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { rtkErrIsValidationError } from "@/shared/lib/errors/validation-error";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Download } from "lucide-react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Offer,
  OfferDocument,
} from "@/services/backend/qpm/offers/types/offer";
import {
  useQpmOfferPdfDownloadMutation,
  useQpmOfferPdfPreviewQuery,
} from "@/services/backend/qpm/offers/api/offer-api";
import { SetOfferImmutableButton } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/set-offer-immutable-button";
import { rtkErrIsErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useNumbersDocumentNumberShowQuery } from "@/services/backend/numbers/api/document-number-api";
import { format } from "date-fns";

type DocumentDownloadOption = {
  document: OfferDocument;
  label: string;
};

const selectableDownloadOptions: DocumentDownloadOption[] = [
  { document: OfferDocument.OFFER, label: t("Angebot") },
  { document: OfferDocument.MonitorCatalog, label: t("Monitor Katalog") },
  {
    document: OfferDocument.QualityAssuranceRef,
    label: t("Mehrwerte, Referenzen & Zertifikate"),
  },
  { document: OfferDocument.ServiceProgram, label: t("Leistungsprogramm") },
  { document: OfferDocument.Contract, label: t("Vertrag") },
];

const documentFileNamePartByKind: Record<OfferDocument, string> = {
  [OfferDocument.ALL]: "Alle-Dokumente",
  [OfferDocument.OFFER]: "Angebot",
  [OfferDocument.MonitorCatalog]: "Monitor-Katalog",
  [OfferDocument.QualityAssuranceRef]: "Mehrwerte-Referenzen-Zertifikate",
  [OfferDocument.ServiceProgram]: "Leistungsprogramm",
  [OfferDocument.Contract]: "Vertrag",
  [OfferDocument.ORDER]: "Order",
};

function sanitizeFileNamePart(value: string): string {
  return value
    .normalize("NFKD")
    .replaceAll(/[\u0300-\u036f]/g, "")
    .replaceAll(/[^a-zA-Z0-9-]+/g, "-")
    .replaceAll(/-+/g, "-")
    .replaceAll(/^-|-$/g, "");
}

export function OfferPdf({ offer }: { offer: Offer }) {
  const [open, setOpen] = useState(false);
  const [pdfTooltipOpen, setPdfTooltipOpen] = useState(false);
  const [pdfTooltipSuppressed, setPdfTooltipSuppressed] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<OfferDocument[]>(
    [],
  );
  const { setValidationError } = useOfferContext();

  useEffect(() => {
    if (!open) {
      setSelectedDocuments([]);
    }
  }, [open]);

  const { data, isLoading, error } = useQpmOfferPdfPreviewQuery(
    {
      offerId: offer.id,
      document: OfferDocument.ALL,
    },
    {
      skip: !open,
    },
  );

  useEffect(() => {
    if (!rtkErrIsValidationError(error)) {
      return;
    }
    setValidationError(error.data);
  }, [error, setValidationError]);

  const setSheetOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    setPdfTooltipOpen(false);
    if (!nextOpen) {
      setPdfTooltipSuppressed(true);
      window.setTimeout(() => setPdfTooltipSuppressed(false), 250);
    }
  };

  return (
    <DocumentSheet open={open} onOpenChange={setSheetOpen}>
      <Tooltip
        open={pdfTooltipOpen}
        onOpenChange={(nextOpen) => {
          if (open || pdfTooltipSuppressed) {
            setPdfTooltipOpen(false);
            return;
          }
          setPdfTooltipOpen(nextOpen);
        }}
      >
        <DocumentSheetTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              onClick={() => {
                setPdfTooltipOpen(false);
              }}
            >
              PDF
            </Button>
          </TooltipTrigger>
        </DocumentSheetTrigger>
        <TooltipContent>
          <p>{t("PDF Vorschau / Herunterladen")}</p>
        </TooltipContent>
      </Tooltip>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Angebot")}
              documentNumber={<DocumentNumberById documentId={offer.id} />}
            />
            <DocumentSheetActionsContent>
              {/* <DocumentNotReadyAlert error={error!} document={t("Angebot")} /> */}
              <DocumentSheetImmutabilityWarning
                title={t("Hinweis")}
                description={t(
                  'Downloads aus einem bearbeitbaren Angebot enthalten das Wasserzeichen "Vorschau". Für die finale Version ohne Wasserzeichen muss das Angebot zuerst festgeschrieben werden.',
                )}
              />
              <SetOfferImmutableButton
                offerId={offer.id}
                immutable={offer.immutable}
                triggerLabel={t("Angebot festschreiben")}
              />
              <Separator />
              <DocumentSheetActionsDownload>
                <DownloadRowWithSpacer>
                  <DownloadAllDocumentsDirectButton
                    offerId={offer.id}
                    offerImmutable={offer.immutable}
                    text={t("Alle Dokumente")}
                    mode="final"
                    className="min-w-0 flex-1 justify-start"
                  />
                </DownloadRowWithSpacer>
                <DownloadRowWithSpacer>
                  <DownloadAllDocumentsDirectButton
                    offerId={offer.id}
                    offerImmutable={offer.immutable}
                    text={t("Vorschau herunterladen")}
                    mode="preview"
                    className="min-w-0 flex-1 justify-start"
                  />
                </DownloadRowWithSpacer>
                {selectableDownloadOptions.map((option) => (
                  <DocumentSelectionDownloadRow
                    key={option.document}
                    option={option}
                    offerId={offer.id}
                    offerImmutable={offer.immutable}
                    selectedDocuments={selectedDocuments}
                    onSelectionChange={setSelectedDocuments}
                  />
                ))}
                <DownloadRowWithSpacer>
                  <DownloadSelectedDocumentsButton
                    offerId={offer.id}
                    offerImmutable={offer.immutable}
                    documents={selectedDocuments}
                    text={t("Ausgewählte Dokumente herunterladen")}
                    className="min-w-0 flex-1 justify-start"
                  />
                </DownloadRowWithSpacer>
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadAllDocumentsDirectButton({
  offerId,
  offerImmutable,
  text,
  mode,
  className,
}: {
  offerId: string;
  offerImmutable: boolean;
  text: string;
  mode: "final" | "preview";
  className?: string;
}) {
  const [download, { isLoading, error, data, reset }] =
    useQpmOfferPdfDownloadMutation();
  const { user } = useAuth();
  const { setValidationError } = useOfferContext();
  const { data: documentNumber } = useNumbersDocumentNumberShowQuery({
    documentId: offerId,
  });

  const finalFilename = (() => {
    const date = format(new Date(), "yyyy-MM-dd");
    const offerNumber = (documentNumber?.string ?? offerId).replaceAll(
      "/",
      "-",
    );
    return `Angebot-GEMEX_${offerNumber}_${date}.pdf`;
  })();

  const previewFilename = (() => {
    const date = format(new Date(), "yyyy-MM-dd");
    const offerNumber = (documentNumber?.string ?? offerId).replaceAll(
      "/",
      "-",
    );
    return `Vorschau_Angebot-${offerNumber}-${date}.pdf`;
  })();

  const resolvedDownloadData =
    mode === "preview" && data
      ? { ...data, filename: previewFilename }
      : mode === "final" && data
        ? { ...data, filename: finalFilename }
        : data;

  useDownloadPdf(resolvedDownloadData, () => {
    reset();
  });

  useEffect(() => {
    if (!(
      rtkErrIsValidationError(error) || rtkErrIsErrDocumentNotReady(error)
    )) {
      return;
    }
    setValidationError(error.data);
  }, [error, setValidationError]);

  const disabled =
    isLoading || (mode === "final" ? !offerImmutable : offerImmutable);

  const disabledHint =
    mode === "final"
      ? t("Nur verfügbar, wenn das Angebot festgeschrieben ist.")
      : t("Die Vorschau ist nach dem Festschreiben nicht mehr verfügbar.");

  const doDownload = () => {
    if (disabled) {
      return;
    }
    download({
      offerId,
      document: OfferDocument.ALL,
      download: mode === "final",
      downloadBy: user?.id ?? "",
    });
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex w-full">
          <Button
            variant="outline"
            className={className ?? "justify-start"}
            disabled={disabled}
            onClick={doDownload}
          >
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{text}</span>
          </Button>
        </span>
      </TooltipTrigger>
      {disabled ? (
        <TooltipContent>
          <p>{disabledHint}</p>
        </TooltipContent>
      ) : null}
    </Tooltip>
  );
}

function DownloadDocumentButton({
  offerId,
  offerImmutable,
  document: documentType,
  text,
  className,
}: {
  offerId: string;
  offerImmutable: boolean;
  document: OfferDocument;
  text: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useQpmOfferPdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  const { setValidationError } = useOfferContext();

  useEffect(() => {
    if (!(
      rtkErrIsValidationError(error) || rtkErrIsErrDocumentNotReady(error)
    )) {
      return;
    }

    setValidationError(error.data);
    // Don't call reset()/close automatically here. If we reset immediately,
    // the dialog can't render the error even once.
  }, [error, setValidationError]);

  const { user } = useAuth();

  const doDownload = (downloadFinal: boolean) => {
    if (isLoading) {
      return;
    }

    download({
      offerId,
      document: documentType,
      download: downloadFinal,
      downloadBy: user?.id ?? "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className={className ?? "justify-start"}>
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Angebot herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Die Vorschau kann jederzeit heruntergeladen werden. Der finale Download ist erst verfügbar, wenn das Angebot festgeschrieben wurde.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert
          error={error}
          onDismiss={() => {
            reset();
          }}
        />
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex w-full">
                <Button
                  onClick={() => doDownload(true)}
                  disabled={isLoading || !offerImmutable}
                  variant="outline"
                  className="w-full"
                >
                  {isLoading ? (
                    <Download className="mr-4 h-5 w-5 animate-bounce" />
                  ) : (
                    <Download className="mr-4 h-5 w-5" />
                  )}
                  <span>{t("Dokument herunterladen")}</span>
                </Button>
              </span>
            </TooltipTrigger>
            {!offerImmutable ? (
              <TooltipContent>
                <p>
                  {t("Nur verfügbar, wenn das Angebot festgeschrieben ist.")}
                </p>
              </TooltipContent>
            ) : null}
          </Tooltip>
          <Button
            onClick={() => doDownload(false)}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Vorschau herunterladen")}</span>
          </Button>
          <DialogClose asChild>
            <Button variant="outline" className="w-full">
              {t("Abbrechen")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DownloadSelectedDocumentsButton({
  offerId,
  offerImmutable,
  documents,
  text,
  className,
}: {
  offerId: string;
  offerImmutable: boolean;
  documents: OfferDocument[];
  text: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [lastDownloadMode, setLastDownloadMode] = useState<"final" | "preview">(
    "preview",
  );
  const [download, { isLoading, error, data, reset }] =
    useQpmOfferPdfDownloadMutation();
  const { user } = useAuth();
  const { setValidationError } = useOfferContext();
  const { data: documentNumber } = useNumbersDocumentNumberShowQuery({
    documentId: offerId,
  });
  const hasSelectedDocuments = documents.length > 0;

  const orderedDocuments = selectableDownloadOptions
    .map((option) => option.document)
    .filter((document) => documents.includes(document));

  const date = format(new Date(), "yyyy-MM-dd");
  const offerNumber = sanitizeFileNamePart(documentNumber?.string ?? offerId);
  const selectedDocumentNames = orderedDocuments
    .map((doc) => documentFileNamePartByKind[doc] ?? doc)
    .map((part) => sanitizeFileNamePart(part))
    .join("-");

  const finalFilename = `Angebot-GEMEX_${offerNumber}_${selectedDocumentNames}_${date}.pdf`;
  const previewFilename = `Vorschau_Angebot-GEMEX_${offerNumber}_${selectedDocumentNames}_${date}.pdf`;
  const resolvedDownloadData = data
    ? {
        ...data,
        filename:
          lastDownloadMode === "preview" ? previewFilename : finalFilename,
      }
    : data;

  useDownloadPdf(resolvedDownloadData, () => {
    setOpen(false);
    reset();
  });

  useEffect(() => {
    if (!(
      rtkErrIsValidationError(error) || rtkErrIsErrDocumentNotReady(error)
    )) {
      return;
    }
    setValidationError(error.data);
  }, [error, setValidationError]);

  const doDownload = (downloadFinal: boolean) => {
    if (isLoading || !hasSelectedDocuments) {
      return;
    }
    if (!downloadFinal && offerImmutable) {
      return;
    }
    setLastDownloadMode(downloadFinal ? "final" : "preview");

    download({
      offerId,
      document: OfferDocument.ALL,
      documents: orderedDocuments,
      download: downloadFinal,
      downloadBy: user?.id ?? "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={hasSelectedDocuments ? "default" : "outline"}
          className={className ?? "justify-start"}
          disabled={!hasSelectedDocuments}
        >
          <Download className="mr-4 h-5 w-5" />
          {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Ausgewählte Dokumente herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Die Vorschau kann jederzeit heruntergeladen werden. Der finale Download ist erst verfügbar, wenn das Angebot festgeschrieben wurde.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert
          error={error}
          onDismiss={() => {
            reset();
          }}
        />
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex w-full">
                <Button
                  onClick={() => doDownload(true)}
                  disabled={
                    isLoading || !offerImmutable || !hasSelectedDocuments
                  }
                  variant="outline"
                  className="w-full"
                >
                  {isLoading ? (
                    <Download className="mr-4 h-5 w-5 animate-bounce" />
                  ) : (
                    <Download className="mr-4 h-5 w-5" />
                  )}
                  <span>{t("Dokument herunterladen")}</span>
                </Button>
              </span>
            </TooltipTrigger>
            {!offerImmutable ? (
              <TooltipContent>
                <p>
                  {t("Nur verfügbar, wenn das Angebot festgeschrieben ist.")}
                </p>
              </TooltipContent>
            ) : null}
          </Tooltip>
          <Button
            onClick={() => doDownload(false)}
            disabled={isLoading || !hasSelectedDocuments || offerImmutable}
            className="w-full"
          >
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Vorschau herunterladen")}</span>
          </Button>
          <DialogClose asChild>
            <Button variant="outline" className="w-full">
              {t("Abbrechen")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentSelectionDownloadRow({
  option,
  offerId,
  offerImmutable,
  selectedDocuments,
  onSelectionChange,
}: {
  option: DocumentDownloadOption;
  offerId: string;
  offerImmutable: boolean;
  selectedDocuments: OfferDocument[];
  onSelectionChange: (documents: OfferDocument[]) => void;
}) {
  const isChecked = selectedDocuments.includes(option.document);

  return (
    <div className="flex w-full items-center gap-2">
      <Checkbox
        checked={isChecked}
        onCheckedChange={(checked) => {
          if (checked) {
            if (selectedDocuments.includes(option.document)) {
              return;
            }
            onSelectionChange([...selectedDocuments, option.document]);
            return;
          }
          onSelectionChange(
            selectedDocuments.filter((doc) => doc !== option.document),
          );
        }}
      />
      <DownloadDocumentButton
        offerId={offerId}
        offerImmutable={offerImmutable}
        document={option.document}
        text={option.label}
        className="min-w-0 flex-1 justify-start"
      />
    </div>
  );
}

function DownloadRowWithSpacer({ children }: { children: ReactNode }) {
  return (
    <div className="flex w-full items-center gap-2">
      <div className="h-4 w-4 shrink-0" aria-hidden />
      {children}
    </div>
  );
}
